import React, { useState, useEffect, useRef } from "react";
import './Gallery.css'
import supabase from "../config/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { TikTokEmbed } from "react-social-media-embed";
import { EditableTikTok } from "./EditableTiktok";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const dragItem = useRef();
  const dragOverItem = useRef();
  const { isTokenValid } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [tiktoks, setTiktoks] = useState([]);
  const [hasPendingTikTokChanges, setHasPendingTikTokChanges] = useState(false);
  const [originalTiktoks, setOriginalTiktoks] = useState([]);
  const [tiktoksToDelete, setTiktoksToDelete] = useState([]);

  const dragTikTokItem = useRef();
  const dragTikTokOverItem = useRef();

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('gallery_images')
        .select('*')
        .order('position', { ascending: true });

      if (error) {
        console.error('Error al obtener imágenes de la tabla:', error.message);
        return;
      }

      if (!data || data.length === 0) {
        setImages([]);
        setOriginalImages([]);
        return;
      }

      const processedFiles = data.map((item) => ({
        id: item.id,
        name: item.name,
        url: item.url,
      }));

      setLoading(false);
      setImages(processedFiles);
      setOriginalImages(processedFiles);
    };

    const fetchTikToks = async () => {
      const { data, error } = await supabase
        .from('gallery_tiktoks')
        .select('*')
        .order('position', { ascending: true });

      if (error) {
        console.error('Error al obtener TikToks:', error.message);
        return;
      }

      setTiktoks(data);
      setOriginalTiktoks(data);
    };

    fetchImages();
    fetchTikToks();
  }, []);


  useEffect(() => {
    const current = JSON.stringify(images);
    const original = JSON.stringify(originalImages);
    setHasPendingChanges(current !== original);
  }, [images, originalImages]);

  useEffect(() => {
    const cleanCurrent = tiktoks.map(v => ({
      id: v.id,
      tiktok_username: v.tiktok_username,
      tiktok_video_id: v.tiktok_video_id,
      position: v.position
    }));

    const cleanOriginal = originalTiktoks.map(v => ({
      id: v.id,
      tiktok_username: v.tiktok_username,
      tiktok_video_id: v.tiktok_video_id,
      position: v.position
    }));

    const current = JSON.stringify(cleanCurrent);
    const original = JSON.stringify(cleanOriginal);

    setHasPendingTikTokChanges(current !== original);
  }, [tiktoks, originalTiktoks]);

  function dragStart(e, position) {
    dragItem.current = position;
  }

  function dragEnter(e, position) {
    dragOverItem.current = position;
  }

  function drop() {
    const copyListItems = [...images];
    const dragItemContent = copyListItems[dragItem.current];
    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;

    const reordered = copyListItems.map((img, index) => ({ ...img, position: index }));
    setImages(reordered);
  }


  function handleDelete(id) {
    setImagesToDelete(prev => [...prev, id]);
    setImages(prev => [...prev.filter(img => img.id !== id)]);
  }

  async function uploadImage(file) {
    if (
      !file ||
      !["image/png", "image/jpeg", "image/jpg", "image/gif"].includes(file.type)
    ) {
      alert(
        "Por favor carga un archivo de imagen válido (PNG, JPG, JPEG, GIF)"
      );
      return null;
    }

    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substr(
      2,
      9
    )}.${fileExt}`;
    const filePath = `gallery/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("images")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Error al subir imagen:", uploadError);
      alert("Hubo un error al subir la imagen.");
      return null;
    }

    const { data: publicUrlData } = supabase.storage
      .from("images")
      .getPublicUrl(filePath);

    return {
      url: publicUrlData.publicUrl,
      name: fileName,
    };
  }

  function handleAdd(file) {
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    const newId = images.length ? Math.max(...images.map(i => i.id)) + 1 : 1;

    setImages(prev => [...prev, { id: newId, url: localUrl, file: file, name: null }]);
  }

  function handleEdit(id, file) {
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    setImages(prev =>
      prev.map(img =>
        img.id === id
          ? { ...img, url: localUrl, file: file, name: null } // Reemplazo la imagen
          : img
      )
    );
  }

  function hasChanges() {
    if (images.length !== originalImages.length) return true;

    for (let i = 0; i < images.length; i++) {
      if (images[i].file) return true; // nueva o editada
      if (images[i].url !== originalImages[i]?.url) return true;
    }

    return false;
  }

  async function saveChanges() {
    for (const id of imagesToDelete) {
      const imgToDelete = originalImages.find(img => img.id === id);
      if (imgToDelete) {
        if (imgToDelete.name) {
          const { error: storageError } = await supabase.storage.from("images").remove([`gallery/${imgToDelete.name}`]);
          if (storageError) {
            alert("Error al eliminar imagen del storage: " + storageError.message);
            return;
          }
        }
        const { error: dbError } = await supabase.from('gallery_images').delete().eq('id', id);
        if (dbError) {
          alert("Error al eliminar imagen de la base de datos: " + dbError.message);
          return;
        }
      }
    }


    const updatedImages = [];

    for (const img of images) {
      if (img.file) {
        const uploaded = await uploadImage(img.file);
        if (!uploaded) {
          alert("Error al subir una imagen, operación cancelada.");
          return;
        }

        const oldImage = originalImages.find(o => o.id === img.id);
        if (oldImage && oldImage.name) {
          await supabase.storage.from("images").remove([`gallery/${oldImage.name}`]);
        }

        if (oldImage) {
          const { error: updateError } = await supabase.from('gallery_images').update({
            url: uploaded.url,
          }).eq('id', img.id);

          if (updateError) {
            alert("Error al actualizar el registro de la imagen: " + updateError.message);
            return;
          }
        } else {
          const { error: insertError } = await supabase.from('gallery_images').insert({
            url: uploaded.url
          });
          if (insertError) {
            alert("Error al insertar el nuevo registro de imagen: " + insertError.message);
            return;
          }
        }

        updatedImages.push({ ...img, url: uploaded.url, file: null });

      } else {
        updatedImages.push(img);
      }

      const { error } = await supabase
        .from('gallery_images')
        .update({ position: img.position })
        .eq('id', img.id);

      if (error) {
        alert("Error al actualizar el orden: " + error.message);
        return;
      }

    }

    setImages(updatedImages);
    setOriginalImages(updatedImages);
    setImagesToDelete([]);
    setHasPendingChanges(false);
  }

  function dragStartTikTok(e, position) {
    dragTikTokItem.current = position;
  }

  function dragEnterTikTok(e, position) {
    dragTikTokOverItem.current = position;
  }

  function dropTikTok() {
    const copyListItems = [...tiktoks];
    const dragItemContent = copyListItems[dragTikTokItem.current];

    copyListItems.splice(dragTikTokItem.current, 1);
    copyListItems.splice(dragTikTokOverItem.current, 0, dragItemContent);

    dragTikTokItem.current = null;
    dragTikTokOverItem.current = null;

    const reordered = copyListItems.map((vid, index) => ({
      ...vid,
      position: index
    }));

    setTiktoks(reordered);
  }

  function handleAddTikTok() {
    const videoUrl = prompt("Pega la URL del TikTok");

    if (!videoUrl) return;

    const match = videoUrl.match(/@([^/]+)\/video\/(\d+)/);

    if (!match) {
      alert("URL inválida");
      return;
    }

    const username = match[1];
    const videoId = match[2];

    setTiktoks(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        tiktok_username: username,
        tiktok_video_id: videoId,
        position: prev.length,
        isNew: true
      }
    ]);
  }

  function handleEditTikTok(id) {
    const videoUrl = prompt("Nueva URL del TikTok");

    if (!videoUrl) return;

    const match = videoUrl.match(/@([^/]+)\/video\/(\d+)/);

    if (!match) {
      alert("URL inválida");
      return;
    }

    setTiktoks(prev =>
      prev.map(v =>
        v.id === id
          ? {
            ...v,
            tiktok_username: match[1],
            tiktok_video_id: match[2],
            isEdited: true
          }
          : v
      )
    );
  }

  function handleDeleteTikTok(id) {
    setTiktoksToDelete(prev => [...prev, id]);
    setTiktoks(prev => prev.filter(v => v.id !== id));
  }

  function discardTikTokChanges() {
    setTiktoks(originalTiktoks);
    setTiktoksToDelete([]);
  }

  async function saveTikTokChanges() {

    for (const id of tiktoksToDelete) {
      await supabase.from('gallery_tiktoks').delete().eq('id', id);
    }

    for (const vid of tiktoks) {

      if (vid.isNew) {
        await supabase.from('gallery_tiktoks').insert({
          tiktok_username: vid.tiktok_username,
          tiktok_video_id: vid.tiktok_video_id,
          position: vid.position
        });
      }

      if (vid.isEdited) {
        await supabase.from('gallery_tiktoks')
          .update({
            tiktok_username: vid.tiktok_username,
            tiktok_video_id: vid.tiktok_video_id,
            position: vid.position
          })
          .eq('id', vid.id);
      }

      if (!vid.isNew && !vid.isEdited) {
        await supabase
          .from('gallery_tiktoks')
          .update({ position: vid.position })
          .eq('id', vid.id);
      }
    }

    setOriginalTiktoks(
      tiktoks.map(v => ({
        ...v,
        isNew: false,
        isEdited: false
      }))
    );
    setTiktoksToDelete([]);
  }

  return (
    <section id="gallery" className="py-4">
      <div className="container d-flex flex-column align-items-center">
        <h2 className="mb-2">Galería</h2>

        {hasChanges() && (
          <div className="mb-4 d-flex gap-3">
            <button className="btn btn-outline-secondary" onClick={() => setImages(originalImages)}>
              Descartar cambios
            </button>
            <button
              type="button"
              className={`btn btn-primary ${!hasPendingChanges ? 'disabled' : ''}`}
              onClick={saveChanges}>
              <i className="fa-solid fa-floppy-disk me-2"></i>
              Guardar cambios
            </button>
          </div>
        )}

        <div className="row mb-4" style={{ width: "100%" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center w-100" style={{ height: "300px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <>
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="col-6 col-lg-4 mb-4"
                  draggable={isTokenValid ? "true" : "false"}
                  onDragStart={(e) => isTokenValid ? dragStart(e, index) : () => { return }}
                  onDragEnter={(e) => isTokenValid ? dragEnter(e, index) : () => { return }}
                  onDragEnd={isTokenValid ? drop : undefined}
                  style={isTokenValid ? { cursor: "move", userSelect: "none" } : {}}
                >
                  <div className="position-relative bg-image shadow-1-strong rounded">
                    {!loadedImages[image.id] && (
                      <div
                        className="position-absolute d-flex justify-content-center align-items-center w-100 h-100"
                        style={{ backgroundColor: "#f0f0f0", zIndex: 1 }}
                      >
                        <div className="spinner-border text-warning" role="status" />
                      </div>
                    )}
                    <img
                      src={image.url}
                      className={`w-100 rounded ${loadedImages[image.id] ? 'fade-in' : ''}`}
                      alt={`Imagen ${image.id}`}
                      style={{ height: "200px", objectFit: "cover" }}
                      onLoad={() => handleImageLoad(image.id)}
                      onError={() => handleImageLoad(image.id)}
                    />
                    {isTokenValid && (
                      <div className="position-absolute" style={{ top: "8px", right: "8px", display: "flex", gap: "8px" }}>
                        <label htmlFor={`edit-file-${image.id}`} className="btn btn-sm btn-light" style={{ padding: "0.25rem 0.5rem", cursor: "pointer" }} title="Editar imagen">
                          ✏️
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          id={`edit-file-${image.id}`}
                          style={{ display: "none" }}
                          onChange={(e) => handleEdit(image.id, e.target.files[0])}
                        />
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(image.id, image.name)} title="Eliminar imagen">
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isTokenValid && (
                <div
                  className="col-6 col-lg-4 mb-4 d-flex justify-content-center align-items-center border rounded"
                  style={{ height: "200px", cursor: "pointer", userSelect: "none" }}
                >
                  <label
                    htmlFor="add-image-file"
                    className="btn btn-outline-primary"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    title="Agregar nueva imagen"
                  >
                    + Agregar imagen
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="add-image-file"
                    style={{ display: "none" }}
                    onChange={(e) => handleAdd(e.target.files[0])}
                  />
                </div>
              )}
            </>
          )}
        </div>


        <h3 className="mb-3">TikToks</h3>

        {isTokenValid && hasPendingTikTokChanges && (
          <div className="mb-3 d-flex gap-3">
            <button
              className="btn btn-outline-secondary"
              onClick={discardTikTokChanges}
            >
              Descartar cambios
            </button>

            <button
              className="btn btn-primary"
              onClick={saveTikTokChanges}
            >
              <i className="fa-solid fa-floppy-disk me-2"></i>
              Guardar TikToks
            </button>
          </div>
        )}

        <div className="row w-100 justify-content-center">

          {tiktoks.map((video, index) => (
            <div
              key={video.id}
              draggable={isTokenValid}
              onDragStart={(e) => isTokenValid && dragStartTikTok(e, index)}
              onDragEnter={(e) => isTokenValid && dragEnterTikTok(e, index)}
              onDragEnd={isTokenValid && dropTikTok}
              className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4 d-flex justify-content-center"
              style={{
                width: '340px',
                flexShrink: 0,
                cursor: isTokenValid ? "move" : "default"
              }}
            >
              <div className="position-relative">

                {isTokenValid ? (
                  <>
                    <EditableTikTok
                      username={video.tiktok_username}
                      videoId={video.tiktok_video_id}
                      isEditing={isTokenValid}
                    />

                    <div
                      className="position-absolute"
                      style={{ top: 5, right: 5, display: 'flex', gap: 5 }}
                    >
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleEditTikTok(video.id)}
                      >
                        ✏️
                      </button>

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTikTok(video.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </>
                ) : (
                  <TikTokEmbed
                    url={`https://www.tiktok.com/@${video.tiktok_username}/video/${video.tiktok_video_id}`}
                    width={325}
                  />
                )}

              </div>
            </div>
          ))}

          {isTokenValid && (
            <div
              className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4 d-flex justify-content-center align-items-center border rounded"
              style={{ height: "300px", cursor: "pointer" }}
              onClick={handleAddTikTok}
            >
              + Agregar TikTok
            </div>
          )}

        </div>

      </div>
    </section>
  );
}